import {
  ICD_REASON,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import { AlgoliaAutocompleteOption } from "core/types";
import Translations from "translations/types";
import { z } from "zod";
import { createMaxLengthMessage } from "./utils";

export const DRG_CODE_CHARACTER_LIMIT = 4;
export const ICD_CODE_CHARACTER_LIMIT = 6;
export const IK_NUMBER_CHARACTER_LIMIT = 9;
export const INFECTIONS_CHARACTER_LIMIT = 150;
export const PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT = 12;
export const SEND_DOCUMENTS_CHARACTER_LIMIT = 34;

const ALLOWED_FLOOR_VALUES = ["u", "0", "h", "s"];

// Time schemas
export const unixDateSchema = z.number().int();
export const AHBdateSchema = z.string().regex(/^\d{8}$/); // YYYYMMDD format (v1.1.0)
export const AHBdateSchemaV2 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/); // YYYY-MM-DD format (v2.0)
export const AHBtimeSchema = z.string().regex(/^\d{6}$/); // HHMMSS format

// Address schemas
export const AHBpostleitzahlSchema = (translations?: Translations) =>
  translations
    ? z.string().max(10, createMaxLengthMessage(translations, 10))
    : z.string().max(10);
export const AHBfloorSchema = (translations?: Translations) =>
  z
    .string()
    .refine(
      (value) =>
        ALLOWED_FLOOR_VALUES.includes(value.toLowerCase()) ||
        /^\d{1,2}$/.test(value),
      {
        message: translations
          ? translations.patientForms.generalForm.sectionI.errorLivingSituation
          : 'Invalid Stockwerk value. Must be "u", "0", "h", "s", or a 1-2 digit number.',
      },
    );

// Yes/no/consent/predicament schemas
export const predicamentSchema = z.union([
  z.literal(PREDICAMENT_STATE_YES),
  z.literal(PREDICAMENT_STATE_NO),
]);

export const predicamentYesNoUnknownSchema = z.union([
  z.literal(PREDICAMENT_STATE_YES),
  z.literal(PREDICAMENT_STATE_NO),
  z.literal(PREDICAMENT_STATE_UNKNOWN),
]);

export const ahbDateinummerSchema = z.coerce.number().int().min(1).max(999999);

/**
 * IK-number Identifies the entire healthcare institution (e.g. hospital, rehab clinic)
 */
export const IKNumberSchema = z
  .union([z.string().length(9), z.number().int().gte(100000000).lte(999999999)])
  .transform((val) => val.toString());
/**
 * Standortnummer identifies a specific location or site of a hospital
 */
export type SiteIdNumber = z.infer<typeof siteIdSchema>;
export const siteIdSchema = z.string().regex(/^\d{1,9}$/, {
  message: "Site Number must be between 1 and 9 digits.",
});

// ICD code schames
export const ICDReasonSchema = z.nativeEnum(ICD_REASON);
export type ICDReason = z.infer<typeof ICDReasonSchema>;
export const ICDCodeSchema = z
  .string()
  .min(3, "ICD-10 code must be at least 3 characters.")
  .max(6, "ICD-10 code must be at most 6 characters.")
  .refine((val) => !val.includes("."), {
    message: "ICD-10 code must not contain dots.",
  });

export type InsuranceAlgoliaHit = {
  careseeker_id?: string;
  id?: string;
  is_active?: boolean;
  name?: string;
  objectID?: string;
};

export type InsuranceAlgoliaOption = AlgoliaAutocompleteOption & {
  careseekerId?: string;
  is_active?: boolean;
};
